import { clsx } from "clsx";
import { type ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { type Accept, useDropzone } from "react-dropzone";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";
import { ActionButton, ActionButtonSize, Callout, Collapse, TriggerButton, TriggerButtonSize } from "widgets";

import { FileUploadTile } from "@/modules/files/components/FileUploadTile.js";
import { default as listStyle } from "@/modules/files/styles/upload.module.scss";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputHint } from "@/ui-modules/core/form/layout/FormInputHint.tsx";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import inputStyle from "@/ui-modules/core/redux-form/styles/FormWidget.module.scss";

import { fieldIsInvalid } from "../../../app-utils/form.js";
import { type Dictionary } from "../../../app-utils/type.js";
import { type IFileUpload } from "../../../redux/modules/file-upload.js";
import style from "../../../styles/callout.module.scss";

export interface ITextareaDropzoneProps extends Partial<WrappedFieldProps> {
  name?: string;
  type?: string; // 'text' | 'password' : Only relevant if componentClass is 'input'
  horizontalLayout?: boolean;
  className?: string;
  placeholder?: string;
  title?: string | ReactElement;
  handleChange?: () => void;
  handleUploadFile?: (file: File, formData: FormData) => void;
  abortFileupload?: (fileName: string) => void;
  fileUploads?: Dictionary<IFileUpload>;
  hotspotId?: string;
  onlyFiles?: boolean;
  singleFile?: boolean;
  accept?: Accept;
  dropzonePlaceholderMessage?: string;
}

export interface DroppedFile extends File {
  path: string;
}

// Ensure single reference for empty dropped file array
const EMPTY_DROPPED_FILE_ARRAY: DroppedFile[] = [];

/**
 * Textarea optionally turning into Dropzone field, to be used in context of redux-form
 */
export default function TextareaDropzone(props: ITextareaDropzoneProps) {
  const {
    singleFile = false,
    handleUploadFile,
    input,
    abortFileupload,
    onlyFiles = false,
    handleChange,
    fileUploads,
    meta,
    title,
    accept,
    horizontalLayout,
    hotspotId,
    placeholder,
    dropzonePlaceholderMessage,
    className,
  } = props;

  const [isDropzoneActive, setDropZoneActive] = useState(onlyFiles);
  const [inputOrFiles, setInputOrFiles] = useState<DroppedFile[]>(
    Array.isArray(input?.value) ? input?.value : EMPTY_DROPPED_FILE_ARRAY,
  ); // injected by Redux Form, retrieve previous file set on onDrop()
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const onDrop = useCallback(
    (files: File[]) => {
      if (singleFile && files.length > 1) {
        files.splice(1, files.length - 1);
      }

      // Apparently we find duplicates only by file names.
      // This is also the key for deleting files, so deleting duplicates, delete them all
      // We can upload folders which contains 2 files with the same names if we remove the next LOCs
      // So we filter duplicates for uploaded files first
      // and as we remove one file, we show the full path on the FileDropzoneItem instead of simple the name.
      const existingFileNames = new Set(inputOrFiles.map((f) => f.name) || []);
      const filesToUpload: DroppedFile[] = files.filter((file) => !existingFileNames.has(file.name)) as DroppedFile[];

      // additional async upload of the file, result will be stored in redux state
      filesToUpload.forEach((file) => {
        const formData: FormData = new FormData();
        formData.append(file.name, file);
        handleUploadFile?.(file, formData);
      });

      // if files are already existing in the drop zone, we merge
      let finalFiles = filesToUpload;
      if (!singleFile && inputOrFiles.length > 0) {
        finalFiles = [...inputOrFiles, ...filesToUpload];
      }

      // redux-form
      input?.onChange(finalFiles);
    },
    [singleFile, handleUploadFile, input, inputOrFiles],
  );

  const { getRootProps, getInputProps, inputRef, isDragActive, open } = useDropzone({
    onDrop,
    accept,
  });

  const deleteFile = useCallback(
    (fileToDelete: { name: string }) => {
      const newListOfFiles = inputOrFiles.filter((file) => fileToDelete.name !== file.name) || [];
      input?.onChange(newListOfFiles); // to update the redux-form state
      abortFileupload?.(fileToDelete.name); // to update the blast state
      if (inputRef.current) {
        // eslint-disable-next-line react-compiler/react-compiler
        inputRef.current.value = "";
      }
    },
    [inputOrFiles, input, abortFileupload, inputRef],
  );

  const isFieldInvalid = fieldIsInvalid(props);

  const dropzoneClasses = useMemo(
    () =>
      clsx({
        "visible-dropzone": isDragActive || (isDropzoneActive && inputOrFiles.length > 0),
        "active-dropzone": isDragActive,
      }),
    [isDragActive, isDropzoneActive, inputOrFiles.length],
  );

  useEffect(() => {
    setDropZoneActive(Array.isArray(input?.value) || onlyFiles);
    setInputOrFiles(Array.isArray(input?.value) ? input?.value : EMPTY_DROPPED_FILE_ARRAY);
  }, [input?.value, onlyFiles]);

  const error: string[] = useMemo(() => {
    return meta?.error instanceof Array ? meta.error : [meta?.error].filter(Boolean);
  }, [meta?.error]);

  return (
    <FormInputGroup horizontalLayout={horizontalLayout} style={{ minWidth: 560 }}>
      {title && <FormLabel hotspotId={hotspotId}>{title}</FormLabel>}

      <FormInputWidget>
        <div className={"textarea-dropzone-input-field"}>
          <FormInputHint align={"start"}>
            <ActionButton
              label={`Select and Import ${singleFile ? "File" : "Files"}`}
              onPress={open}
              size={ActionButtonSize.Small}
            />{" "}
            or drag and drop {singleFile ? "it" : "them"} in the area below.{" "}
            {!singleFile && <em>Each file must have a unique name.</em>}
          </FormInputHint>
          <div
            className={clsx("dropzone-area", dropzoneClasses)}
            {...getRootProps({
              onClick: (event) => event.stopPropagation(),
            })}
          >
            <input {...getInputProps()} ref={inputRef} />
            <div className={clsx("dropzone", dropzoneClasses)} key="dropzone">
              {inputOrFiles.length === 0 && (
                <div className="dz-default dz-message">
                  {dropzonePlaceholderMessage ?? `Drop ${singleFile ? "file" : "files"} here to upload`}
                </div>
              )}
              <div className={listStyle.list}>
                {isDropzoneActive &&
                  inputOrFiles.length > 0 &&
                  inputOrFiles.map((file: File, i) => (
                    <FileUploadTile
                      file={{ fileName: file.name, fileSize: file.size }}
                      errors={fileUploads?.[file.name]?.errors ?? []}
                      key={`${i}-${file.name}`}
                      uploadStatus={fileUploads?.[file.name]?.uploadStatus}
                      progress={fileUploads?.[file.name]?.progress}
                      cancelUploadRequest={(file) => deleteFile({ name: file.fileName })}
                      clearFile={(file) => deleteFile({ name: file.fileName })}
                    />
                  ))}
              </div>
            </div>
            <textarea
              {...input}
              className={clsx(`${className} textarea-input`, inputStyle.input_widget, {
                [inputStyle.input_error]: isFieldInvalid,
                "font-monospace": !isDropzoneActive,
              })}
              placeholder={placeholder}
              rows={10}
              onChange={useCallback(
                (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  input?.onChange(e);
                  handleChange?.();
                },
                [input, handleChange],
              )}
            />
          </div>
          {isFieldInvalid &&
            (error.length > 1 ? (
              <FormError>
                <div style={{ display: "flex" }}>
                  <div style={{ flex: 1 }}>{error[0]}</div>
                  <TriggerButton
                    label={isExpanded ? "Hide all errors" : "Show all errors"}
                    onPress={() => setIsExpanded(!isExpanded)}
                    icon={isExpanded ? "unfold_less_double" : "unfold_more_double"}
                    size={TriggerButtonSize.Small}
                  />
                </div>
                <Collapse isExpanded={isExpanded}>
                  {error.slice(1).map((err, index) => (
                    <Callout key={`${index}-error`} className={clsx(style.oldError)}>
                      {err}
                    </Callout>
                  ))}
                </Collapse>
              </FormError>
            ) : (
              <FormError>{error[0]}</FormError>
            ))}
        </div>
      </FormInputWidget>
    </FormInputGroup>
  );
}

export function TextareaDropzoneReduxFormField(props: ITextareaDropzoneProps & BaseFieldProps) {
  return <Field {...props} />;
}
